import {FormApi} from 'final-form';
import i18next from 'i18next';
import {showNotification, Wizard} from 'platform/components';

import {useEffect, useMemo} from 'react';
import {useWatch} from 'react-hook-form';

import {ConditionEnum, VehicleTypeEnum} from '@dms/api/vehicle';
import {
  useVehicleFormContext,
  useWizardTabRouting,
  useFormKeyboardNavigation,
  SaleVehicle,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {FieldAvailabilityContext, FullScreenModal} from '@dms/shared';
import {TConditionForm} from '@dms/teas';

import {getSafely} from 'shared/web';

import {AuditWrapper} from '../(sections)/AuditWrapper';
import {SaleBasicInformation} from '../(sections)/SaleBasicInformation';
import {SaleFeatures} from '../(sections)/SaleFeatures';
import {SalePhotos} from '../(sections)/SalePhotos';
import {SaleVehicleData} from '../(sections)/SaleVehicleData';
import {useSaleVehicleWizard} from '../hooks/useSaleVehicleWizard';

interface ModalKeys {
  headline?: string;
  create?: string;
}

interface CreateSaleVehicleFormProps {
  isCreating: boolean;
  onSave: (values: SaleVehicle) => Promise<void>;
  onVehicleTypeChange: (type: VehicleTypeEnum, condition?: ConditionEnum) => void;
  onFuelTypeChange?: (fuelTypeCondition: FieldAvailabilityContext) => void;
  onDiscard: () => void;
  tabRoutePattern?: keyof typeof vehiclesRoutes;
  modalKeys?: ModalKeys;
  vehicleId?: string;
  disableVinDecoder?: boolean;
  infoAlert?: {
    title: string;
    message: string;
  };
}

const AuditMountWrapper = ({isMounted, vehicleId}: {isMounted: boolean; vehicleId?: string}) =>
  isMounted ? <AuditWrapper vehicleId={vehicleId} /> : null;

export const SaleVehicleForm = ({
  onSave,
  onDiscard,
  isCreating,
  onVehicleTypeChange,
  onFuelTypeChange,
  tabRoutePattern = 'createV2Tab',
  vehicleId,
  modalKeys = {},
  disableVinDecoder = false,
  infoAlert,
}: CreateSaleVehicleFormProps) => {
  const {control, formApi, auditContext} = useVehicleFormContext();

  const tabStepMap = useMemo(
    () => ({
      'basic-information': 0,
      'vehicle-data': 1,
      features: 2,
      photos: 3,
      condition: 4,
    }),
    []
  );

  const {wizardRef, onStepChange, currentTab} = useWizardTabRouting({
    tabStepMap,
    tabRoutePattern: getSafely(vehiclesRoutes, tabRoutePattern),
    defaultTab: 'basic-information',
  });

  const {onKeyDownCapture} = useFormKeyboardNavigation({
    wizardRef,
    onStepChange,
  });

  const wizardSteps = useSaleVehicleWizard(control, {
    basicInformation: (
      <SaleBasicInformation disableVinDecoder={disableVinDecoder} infoAlert={infoAlert} />
    ),
    vehicleData: <SaleVehicleData />,
    features: <SaleFeatures />,
    condition: <AuditMountWrapper isMounted={currentTab === 'condition'} vehicleId={vehicleId} />,
    photos: <SalePhotos />,
  });

  const vehicleTypeWatch = useWatch({
    control,
    name: 'type',
  });

  const vehicleConditionWatch = useWatch({
    control,
    name: 'condition',
  });

  const fuelTypeWatch = useWatch<SaleVehicle, 'fuelType'>({
    control,
    name: 'fuelType',
  });

  const hybridTypeWatch = useWatch<SaleVehicle, 'hybridType'>({
    control,
    name: 'hybridType',
  });

  useEffect(() => {
    onVehicleTypeChange(vehicleTypeWatch?.[0] ?? '', vehicleConditionWatch?.[0]);
    // onVehicleTypeChange is stable (prop from parent); if it changes often, wrap in useCallback in parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeWatch, vehicleConditionWatch]);

  useEffect(() => {
    if (!onFuelTypeChange) {
      return;
    }
    // Skip the initial call before the form hydrates default values
    if (fuelTypeWatch === undefined && hybridTypeWatch === undefined) {
      return;
    }
    onFuelTypeChange({
      fuelType: fuelTypeWatch?.at(0),
      hybridType: hybridTypeWatch?.at(0),
    });
    // onFuelTypeChange is stable (prop from parent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuelTypeWatch, hybridTypeWatch]);

  return (
    <FullScreenModal
      data-testid="saleVehicleForm"
      headline={modalKeys.headline ?? i18n.t('entity.vehicle.labels.createVehicle')}
      scrollable={currentTab === 'condition' ? true : false}
      backgroundColor="general.white"
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isCreating,
          onClick: onDiscard,
        },
        {
          title: modalKeys.create ?? i18n.t('general.actions.create'),
          isLoading: isCreating,
          onClick: () => {
            formApi.handleSubmit(
              (values) => {
                const auditForm = auditContext.getForm() as FormApi<TConditionForm>;
                const auditAssets = auditContext.getAssets();

                if (auditForm && 'getState' in auditForm) {
                  const auditValues = auditForm.getState().values;
                  values.auditData = auditValues;
                }
                if (auditAssets) {
                  values.auditAssets = auditAssets;
                }

                onSave(values).then(() => {
                  auditContext.setForm(null);
                  auditContext.setAssets({});
                });
              },
              (error) => {
                if (i18next.exists('errors.invalid_submit')) {
                  showNotification.warning(i18next.t('errors.invalid_submit'));
                }

                if (process.env.ENV_TYPE === 'dev') {
                  console.error(error);
                }
              }
            )();
          },
        },
      ]}
    >
      <Wizard
        ref={wizardRef}
        orientation="horizontal"
        steps={wizardSteps}
        height="100%"
        onStepChange={onStepChange}
        onKeyDownCapture={onKeyDownCapture}
      />
    </FullScreenModal>
  );
};
